import { useRef, useState } from 'react';
import {
  buildCalendarCells,
  today,
  WEEKDAY_LABELS,
  type CalendarUnit,
} from '@/tasks/calendar-range';
import { groupByDueDate, type Task } from '@/tasks/task';
import { formatClockLabel, parseTime, type DraftRange, type DropTarget } from '@/tasks/time-grid';
import { calendarColor, eventStyle, EVENT_CLASS } from './KindLabel';
import { TimeGridCalendar } from './TimeGridCalendar';

/**
 * TaskCalendar の props。
 */
export interface TaskCalendarProps {
  /** 基準日（`YYYY-MM-DD`） */
  baseDate: string;
  /** 表示単位 */
  unit: CalendarUnit;
  /** 表示するタスク。範囲外は描画されない */
  tasks: readonly Task[];
  /** タスクを選んだときに呼ばれる */
  onSelect?: (task: Task) => void;
  /** タスクを別の日へ動かしたときに呼ばれる */
  onMove?: (task: Task, target: DropTarget) => void;
  /** 端をつまんで時間の長さを変えたときに呼ばれる */
  onResize?: (task: Task) => void;
  /** 空いているところを引いて、新しい予定を作ったときに呼ばれる */
  onCreate?: (range: DraftRange) => void;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクをカレンダーに並べる。週と日は 15 分刻みの時間軸、月は日セルのグリッドで表示する
 * @graph-connects buildCalendarCells [calls] 表示単位に応じた日付を計算する
 * @graph-connects TimeGridCalendar [calls] 週・日を時間軸で描画する
 * @graph-connects groupByDueDate [calls] 期日ごとにタスクをまとめる
 * @graph-connects eventStyle [calls] 終日の予定の面の色を引く
 * @graph-connects calendarColor [calls] 時刻のある予定の点の色を引く
 */
export function TaskCalendar({
  baseDate,
  unit,
  tasks,
  onSelect,
  onMove,
  onResize,
  onCreate,
}: TaskCalendarProps) {
  const cells = buildCalendarCells(baseDate, unit);
  // 掴んでいるタスク。月表示は日付だけを差し替えるので、時刻はそのまま持ち越す
  const dragging = useRef<Task | null>(null);
  const [hoverDate, setHoverDate] = useState('');

  // 週・日は時間軸で見る。予定が何時から何時までかは、経理の日々の段取りでは
  // 「その日にあるか」より重要なため
  if (unit !== 'month') {
    return (
      <TimeGridCalendar
        cells={cells}
        tasks={tasks}
        onSelect={onSelect}
        onMove={onMove}
        onResize={onResize}
        onCreate={onCreate}
      />
    );
  }

  const byDate = groupByDueDate(tasks);
  const todayDate = today();

  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <div className="min-w-[640px]">
        <div className="sticky top-0 z-1 grid grid-cols-7 border-b border-[var(--color-cal-line)] bg-[var(--color-bg-base)]">
          {WEEKDAY_LABELS.map((label) => (
            <div
              key={label}
              className="py-2 text-center text-sm font-medium text-[var(--color-text-muted)]"
            >
              {label}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {cells.map((cell) => {
            const dayTasks = byDate.get(cell.date) ?? [];
            return (
              <div
                key={cell.date}
                data-drop-date={cell.date}
                onDragOver={(event) => {
                  if (onMove !== undefined) {
                    event.preventDefault();
                    // `dragOver` は止めていても飛び続けるので、変わったときだけ差し替える
                    setHoverDate((current) => (current === cell.date ? current : cell.date));
                  }
                }}
                onDragLeave={() => {
                  setHoverDate((current) => (current === '' ? current : ''));
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  const task = dragging.current;
                  dragging.current = null;
                  setHoverDate('');
                  // 月表示は日付だけを動かす。時刻は持ち越すので keepTime を立てる
                  if (task !== null && onMove !== undefined && task.dueDate !== cell.date) {
                    onMove(task, { date: cell.date, keepTime: true });
                  }
                }}
                className={`min-h-32 border-r border-b border-[var(--color-cal-line)] p-1.5 [&:nth-child(7n)]:border-r-0 ${
                  hoverDate === cell.date ? 'bg-[var(--color-doing-bg)]' : ''
                }`}
              >
                {/* Google カレンダーと同じく、日付はマスの上の中央に置き、今日は丸で塗る */}
                <div className="mb-1 flex justify-center">
                  <span
                    aria-current={cell.date === todayDate ? 'date' : undefined}
                    className={`flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-xs tabular-nums ${
                      cell.date === todayDate
                        ? 'bg-[var(--color-cal-today)] text-white'
                        : cell.inFocus
                          ? 'text-[var(--color-text-primary)]'
                          : 'text-[var(--color-text-muted)] opacity-60'
                    }`}
                  >
                    {cell.day}
                  </span>
                </div>
                <ul className="m-0 list-none space-y-px p-0">
                  {dayTasks.map((task) => (
                    <li key={task.id}>
                      <button
                        type="button"
                        draggable={onMove !== undefined}
                        onDragStart={(event) => {
                          dragging.current = task;
                          event.dataTransfer.effectAllowed = 'move';
                          event.dataTransfer.setData('text/plain', task.id);
                        }}
                        onDragEnd={() => {
                          dragging.current = null;
                          setHoverDate('');
                        }}
                        title={`${task.kind}／${task.assignee}／${task.title}`}
                        onClick={() => {
                          onSelect?.(task);
                        }}
                        style={startOf(task) === null ? eventStyle(task) : undefined}
                        className={
                          startOf(task) === null
                            ? // 終日の予定は Google カレンダーと同じく面を塗った帯にする
                              `block w-full truncate rounded px-1.5 py-px text-left text-xs font-medium ${EVENT_CLASS[task.status]}`
                            : // 時刻のある予定は、色の点・時刻・内容を 1 行に並べる（面は塗らない）
                              `flex w-full items-center gap-1 truncate rounded px-1 py-px text-left text-xs hover:bg-[var(--color-bg-subtle)] ${
                                task.status === '対応済'
                                  ? 'text-[var(--color-cal-label)] line-through'
                                  : cell.date < todayDate
                                    ? 'text-[var(--color-cal-label)]'
                                    : 'text-[#3c4043]'
                              }`
                        }
                      >
                        {startOf(task) === null ? (
                          task.title
                        ) : (
                          <>
                            <span
                              aria-hidden="true"
                              className="h-2 w-2 shrink-0 rounded-full"
                              style={{ backgroundColor: calendarColor(task.kind) }}
                            />
                            <span className="shrink-0 text-[var(--color-text-secondary)]">
                              {formatClockLabel(startOf(task) ?? 0)}
                            </span>
                            <span className="truncate font-medium">{task.title}</span>
                          </>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 予定の開始時刻を 0 時からの分で返す。時刻の無い（終日の）予定は null
 * @graph-connects parseTime [calls] 開始時刻を読む
 */
function startOf(task: Task): number | null {
  return parseTime(task.startTime);
}
